"use client";

import { Calendar as CalendarIcon, X, ChevronRight } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { DateRange } from "react-day-picker";
import { format, subDays, startOfMonth, endOfMonth, subMonths, startOfYear, startOfWeek, endOfWeek, differenceInDays } from "date-fns";

import { Calendar } from "@/app/_module/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/_module/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { Button } from "@/app/_module/components/ui/button";
import { cn } from "@/app/_module/lib/utils";
import { nextDayRange } from "@/app/_module/utils/dayRangeClick";

export interface CustomDateRange {
  from: Date;
  to: Date;
}

interface DateRangeSelectorProps {
  selectedRange: string;
  onRangeChange: (range: string) => void;
  onCustomDateChange?: (dateRange: CustomDateRange | undefined) => void;
  customDateRange?: CustomDateRange;
}

// Quick preset ranges for easy selection
const getPresetRanges = () => {
  const today = new Date();
  return [
    { label: "Today", from: today, to: today },
    { label: "Yesterday", from: subDays(today, 1), to: subDays(today, 1) },
    { label: "Last 7 Days", from: subDays(today, 6), to: today },
    { label: "Last 14 Days", from: subDays(today, 13), to: today },
    { label: "Last 30 Days", from: subDays(today, 29), to: today },
    { label: "This Week", from: startOfWeek(today, { weekStartsOn: 0 }), to: endOfWeek(today, { weekStartsOn: 0 }) },
    { label: "Last Week", from: startOfWeek(subDays(today, 7), { weekStartsOn: 0 }), to: endOfWeek(subDays(today, 7), { weekStartsOn: 0 }) },
    { label: "This Month", from: startOfMonth(today), to: endOfMonth(today) },
    { label: "Last Month", from: startOfMonth(subMonths(today, 1)), to: endOfMonth(subMonths(today, 1)) },
    { label: "This Year", from: startOfYear(today), to: today },
  ];
};

export const DateRangeSelector = function({
  selectedRange,
  onRangeChange,
  onCustomDateChange,
  customDateRange,
}: DateRangeSelectorProps) {
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    customDateRange ? { from: customDateRange.from, to: customDateRange.to } : undefined
  );
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [rangeAnchor, setRangeAnchor] = useState<Date | null>(null);
  const presetRanges = useMemo(() => getPresetRanges(), []);

  // Sync internal state with prop
  useEffect(() => {
    if (customDateRange) {
      setDateRange({ from: customDateRange.from, to: customDateRange.to });
    }
  }, [customDateRange]);

  // "All Time" was removed (Jun 2026) — it sent startDate=2000-01-01
  // and OOM-killed the platform service when the recursive agency-stats
  // queries tried to scan 26 years of leads. The backend now clamps to
  // a 13-month max window anyway; offering "All Time" as a label was
  // misleading. 1 Year is the max range any preset offers.
  const dateRanges = [
    { value: "this-month", label: "This Month" },
    { value: "6-months", label: "6 Months" },
    { value: "ytd", label: "Year to Date" },
    { value: "12-months", label: "1 Year" },
    { value: "custom", label: "Custom Range" },
  ];

  // Calendar bounds — keep them aligned with the backend clamp in
  // src/utils/stats-date-range.util.ts. Custom range can't go more than
  // 13 months back (matches the "12-months" period max) and can't pick
  // a future date.
  const calendarBounds = useMemo(() => {
    const today = new Date();
    const earliest = new Date(today);
    earliest.setMonth(earliest.getMonth() - 13);
    earliest.setHours(0, 0, 0, 0);
    return { before: earliest, after: today };
  }, []);

  const formatDateRange = (range: DateRange | undefined) => {
    if (!range?.from) return "Select dates";
    if (!range.to) return format(range.from, "MMM d, yyyy");
    // Same day
    if (range.from.toDateString() === range.to.toDateString()) {
      return format(range.from, "MMM d, yyyy");
    }
    // Same month & year
    if (range.from.getMonth() === range.to.getMonth() && range.from.getFullYear() === range.to.getFullYear()) {
      return `${format(range.from, "MMM d")} - ${format(range.to, "d, yyyy")}`;
    }
    // Same year
    if (range.from.getFullYear() === range.to.getFullYear()) {
      return `${format(range.from, "MMM d")} - ${format(range.to, "MMM d, yyyy")}`;
    }
    // Different years
    return `${format(range.from, "MMM d, yyyy")} - ${format(range.to, "MMM d, yyyy")}`;
  };

  // Calculate days selected for display
  const daysSelected = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return null;
    const days = differenceInDays(dateRange.to, dateRange.from) + 1;
    return days === 1 ? "1 day" : `${days} days`;
  }, [dateRange]);

  // First click = that day (applied at once), second = the span — see nextDayRange.
  const handleDateSelect = (_range: DateRange | undefined, clicked: Date) => {
    const next = nextDayRange(rangeAnchor, clicked);
    setRangeAnchor(next.anchor);
    setDateRange(next.range);
    onCustomDateChange?.(next.range);
    if (next.done) setIsCalendarOpen(false);
  };

  const handlePresetSelect = (preset: { from: Date; to: Date }) => {
    const range = { from: preset.from, to: preset.to };
    setDateRange(range);
    if (onCustomDateChange) {
      onCustomDateChange(range);
    }
    setIsCalendarOpen(false);
  };

  const handleClearRange = () => {
    setDateRange(undefined);
    if (onCustomDateChange) {
      onCustomDateChange(undefined);
    }
  };

  const handleRangeChange = (value: string) => {
    onRangeChange(value);
    // Clear custom date range when switching away from custom
    if (value !== "custom" && onCustomDateChange) {
      onCustomDateChange(undefined);
      setDateRange(undefined);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
      {/* Only show calendar when "Custom Range" is selected */}
      {selectedRange === "custom" && (
        <Popover onOpenChange={(open) => { setIsCalendarOpen(open); setRangeAnchor(null); }} open={isCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              className={cn(
                // m-daterange-btn: shell CSS hook — its presence tells the /m
                // layout that custom mode is on (two controls, needs a row).
                "m-daterange-btn w-full sm:w-auto justify-start text-left font-normal h-9",
                !dateRange && "text-muted-foreground"
              )}
              variant="outline"
            >
              <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
              <span className="truncate">{formatDateRange(dateRange)}</span>
              {daysSelected && (
                <span className="ml-2 text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded hidden sm:inline">
                  {daysSelected}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            collisionPadding={12}
            sideOffset={4}
            // m-daterange-pop: hook for the native shell's CSS (single-month
            // calendar, viewport-fitted panel). Desktop is unaffected.
            className="m-daterange-pop w-auto max-w-[calc(100vw-24px)] p-0"
          >
            <div className="flex flex-col sm:flex-row">
              {/* Preset shortcuts sidebar */}
              <div className="border-b sm:border-b-0 sm:border-r p-2 sm:w-40 flex-shrink-0">
                <div className="text-xs font-medium text-muted-foreground px-2 py-1.5 hidden sm:block">Quick Select</div>
                <div className="flex flex-wrap sm:flex-col gap-1">
                  {presetRanges.map((preset) => (
                    <Button
                      key={preset.label}
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "justify-start text-xs h-7 px-2 font-normal",
                        dateRange?.from?.toDateString() === preset.from.toDateString() &&
                        dateRange?.to?.toDateString() === preset.to.toDateString() &&
                        "bg-primary/10 text-primary"
                      )}
                      onClick={() => handlePresetSelect(preset)}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Calendar */}
              <div className="p-2">
                <Calendar
                  defaultMonth={dateRange?.from || new Date()}
                  mode="range"
                  // One month on phone-width viewports — two months overflow the
                  // popover sideways and made the panel glitch/clip on iOS.
                  numberOfMonths={typeof window !== "undefined" && window.innerWidth < 640 ? 1 : 2}
                  onSelect={handleDateSelect}
                  selected={dateRange}
                  // Disable dates outside the supported window so users can't
                  // select past the backend's 13-month clamp. The chevrons to
                  // navigate further-back months still work, but the days
                  // themselves render disabled.
                  disabled={calendarBounds}
                  fromDate={calendarBounds.before}
                  toDate={calendarBounds.after}
                  className="[&_.rdp-months]:flex-col sm:[&_.rdp-months]:flex-row [&_.rdp-month]:w-full"
                  showOutsideDays={false}
                />

                {/* Footer with selected range info and actions */}
                <div className="border-t mt-2 pt-2 px-1 flex items-center justify-between">
                  <div className="text-xs text-muted-foreground">
                    {dateRange?.from && dateRange?.to ? (
                      <span className="flex items-center gap-1">
                        <span>{format(dateRange.from, "MMM d, yyyy")}</span>
                        <ChevronRight className="h-3 w-3" />
                        <span>{format(dateRange.to, "MMM d, yyyy")}</span>
                        {daysSelected && <span className="text-primary font-medium ml-1">({daysSelected})</span>}
                      </span>
                    ) : dateRange?.from ? (
                      <span>Select end date</span>
                    ) : (
                      <span>Select start date</span>
                    )}
                  </div>
                  {dateRange && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                      onClick={handleClearRange}
                    >
                      <X className="h-3 w-3 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
      <Select onValueChange={handleRangeChange} value={selectedRange}>
        <SelectTrigger className="w-full sm:w-48 h-9">
          <SelectValue placeholder="Select date range" />
        </SelectTrigger>
        <SelectContent>
          {dateRanges.map((range) => (
            <SelectItem key={range.value} value={range.value}>
              {range.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
